export type DifficultyLevel = 'foundational' | 'intermediate' | 'advanced';
export type CraftModuleStatus = 'none' | 'processing' | 'completed' | 'error';

export type CraftModuleTabKey =
  | 'core_concept'
  | 'why_it_matters'
  | 'what_good_looks_like'
  | 'common_mistakes'
  | 'real_world_application'
  | 'role_segment_adaptation'
  | 'frameworks_talk_tracks'
  | 'practice_reinforcement'
  | 'metrics_outcomes'
  | 'evolution_updates';

export interface CraftContentSection {
  heading: string;
  body: string;
  callout_type?: 'tip' | 'warning' | 'example' | 'key_insight';
  callout_text?: string;
}

export interface CraftCitation {
  author: string;
  title: string;
  publication: string;
  year: string;
  url?: string;
}

export interface CraftInsight {
  insight: string;
  impact_level: 'high' | 'medium' | 'low';
  confidence_score: number;
  sales_translation: string;
}

export interface TabCoreConcept {
  overview: string;
  sections: CraftContentSection[];
  key_principles: string[];
  citations: CraftCitation[];
}

export interface TabWhyItMatters {
  strategic_importance: string;
  commercial_impact: CraftInsight[];
  cost_of_ignorance: string;
  career_impact: string;
}

export interface ExcellenceStandard {
  dimension: string;
  poor: string;
  average: string;
  excellent: string;
  observable_behaviours: string[];
}

export interface TabWhatGoodLooksLike {
  summary: string;
  standards: ExcellenceStandard[];
  exemplar_profile: string;
}

export interface CommonMistake {
  mistake: string;
  severity: 'critical' | 'moderate' | 'minor';
  why_it_happens: string;
  how_to_fix: string;
  level_most_affected: DifficultyLevel;
}

export interface TabCommonMistakes {
  summary: string;
  mistakes: CommonMistake[];
  misconceptions: string[];
}

export interface RealWorldScenario {
  title: string;
  context: string;
  approach: string;
  outcome: string;
  lesson: string;
}

export interface TabRealWorldApplication {
  summary: string;
  scenarios: RealWorldScenario[];
  deal_examples: string[];
}

export interface RoleAdaptation {
  role: string;
  how_it_applies: string;
  key_differences: string;
  focus_areas: string[];
}

export interface SegmentAdaptation {
  segment: string;
  how_it_differs: string;
  key_adjustments: string[];
}

export interface SectorNote {
  sector: string;
  adaptation: string;
}

export interface TabRoleSegmentAdaptation {
  role_adaptations: RoleAdaptation[];
  segment_adaptations: SegmentAdaptation[];
  motion_notes: { motion: string; note: string }[];
  sector_notes: SectorNote[];
}

export interface TalkTrack {
  label: string;
  context: string;
  track: string;
}

export interface FrameworkReference {
  name: string;
  relevance: string;
  when_to_use: string;
  when_not_to_use: string;
}

export interface ExecutionTemplate {
  title: string;
  steps: string[];
  tips: string[];
}

export interface TabFrameworksTalkTracks {
  frameworks: FrameworkReference[];
  talk_tracks: TalkTrack[];
  execution_templates: ExecutionTemplate[];
}

export interface PracticeExercise {
  title: string;
  type: 'drill' | 'reflection' | 'role_play' | 'written' | 'observation';
  instructions: string;
  expected_duration_minutes: number;
  coaching_cue: string;
}

export interface TabPracticeReinforcement {
  exercises: PracticeExercise[];
  manager_coaching_notes: string[];
  reinforcement_schedule: string;
}

export interface MasteryMetric {
  metric: string;
  how_to_measure: string;
  baseline: string;
  target: string;
  leading_indicators: string[];
}

export interface TabMetricsOutcomes {
  metrics: MasteryMetric[];
  observable_outcomes: string[];
  assessment_criteria: string;
}

export interface QuarterlyModuleChange {
  change: string;
  classification: 'new' | 'updated' | 'deprecated';
  impact_level: 'high' | 'medium' | 'low';
  rationale: string;
}

export interface TabEvolutionUpdates {
  last_reviewed: string;
  changes_this_quarter: QuarterlyModuleChange[];
  emerging_practices: string[];
  deprecated_advice: { advice: string; reason: string; replacement: string }[];
  confidence_score: number;
  recency_score: number;
}

export interface CraftModuleRecord {
  id: string;
  slug: string;
  category_slug: string;
  title: string;
  subtitle: string;
  description: string;
  difficulty_level: DifficultyLevel;
  estimated_duration_minutes: number;
  applicable_roles: string[];
  applicable_segments: string[];
  applicable_motions: string[];
  relevant_sectors: string[];
  prerequisite_modules: string[];
  related_modules: string[];
  tags: string[];
  icon_name: string;
  display_order: number;
  tab_core_concept: TabCoreConcept;
  tab_why_it_matters: TabWhyItMatters;
  tab_what_good_looks_like: TabWhatGoodLooksLike;
  tab_common_mistakes: TabCommonMistakes;
  tab_real_world_application: TabRealWorldApplication;
  tab_role_segment_adaptation: TabRoleSegmentAdaptation;
  tab_frameworks_talk_tracks: TabFrameworksTalkTracks;
  tab_practice_reinforcement: TabPracticeReinforcement;
  tab_metrics_outcomes: TabMetricsOutcomes;
  tab_evolution_updates: TabEvolutionUpdates;
  status: CraftModuleStatus;
  current_stage: number;
  version: number;
  quarter_label: string;
  raw_research: string;
  error_message: string;
  generated_at: string | null;
  confidence_score: number;
  recency_score: number;
  business_relevance_score: number;
  created_at: string;
  updated_at: string;
}

export interface CraftCategoryRecord {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon_name: string;
  display_order: number;
  module_count: number;
  status: CraftModuleStatus;
  created_at: string;
  updated_at: string;
}

export interface CraftLearningPath {
  id: string;
  slug: string;
  title: string;
  description: string;
  intended_audience: string;
  learning_objective: string;
  module_sequence: { module_slug: string; rationale: string }[];
  estimated_total_minutes: number;
  assessment_approach: string;
  icon_name: string;
  status: CraftModuleStatus;
  current_stage: number;
  version: number;
  quarter_label: string;
  generated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserCraftProgress {
  id: string;
  user_id: string;
  module_slug: string;
  article_slug: string;
  status: 'not_started' | 'in_progress' | 'completed';
  completed_at: string | null;
  bookmarked: boolean;
  created_at: string;
  updated_at: string;
}

export interface CraftReviewLog {
  id: string;
  review_type: 'quarterly_refresh' | 'content_addition' | 'accuracy_check';
  summary: string;
  changes_made: { type: string; description: string }[];
  changes_this_quarter: Record<string, unknown>;
  deprecated_guidance: unknown[];
  new_opportunities: unknown[];
  modules_updated: string[];
  reviewed_at: string;
  next_review_at: string;
  created_at: string;
}

export const CRAFT_MODULE_TABS: { key: CraftModuleTabKey; label: string; shortLabel: string }[] = [
  { key: 'core_concept', label: 'Core Concept', shortLabel: 'Concept' },
  { key: 'why_it_matters', label: 'Why It Matters', shortLabel: 'Why' },
  { key: 'what_good_looks_like', label: 'What Good Looks Like', shortLabel: 'Excellence' },
  { key: 'common_mistakes', label: 'Common Mistakes', shortLabel: 'Mistakes' },
  { key: 'real_world_application', label: 'Real-World Application', shortLabel: 'Application' },
  { key: 'role_segment_adaptation', label: 'Role & Segment', shortLabel: 'Adaptation' },
  { key: 'frameworks_talk_tracks', label: 'Frameworks & Talk Tracks', shortLabel: 'Frameworks' },
  { key: 'practice_reinforcement', label: 'Practice & Drills', shortLabel: 'Practice' },
  { key: 'metrics_outcomes', label: 'Metrics & Outcomes', shortLabel: 'Metrics' },
  { key: 'evolution_updates', label: 'Evolution & Updates', shortLabel: 'Evolution' },
];

export const AVAILABLE_CRAFT_CATEGORIES = [
  { slug: 'sales-gtm-foundations', title: 'Sales & GTM Foundations', icon: 'GraduationCap' },
  { slug: 'prospecting-pipeline', title: 'Prospecting & Pipeline Generation', icon: 'Phone' },
  { slug: 'cold-calling', title: 'Cold Calling', icon: 'PhoneCall' },
  { slug: 'messaging-outreach', title: 'Messaging, Email & Social Outreach', icon: 'Mail' },
  { slug: 'discovery-qualification', title: 'Discovery, Qualification & Buyer Understanding', icon: 'Search' },
  { slug: 'value-selling', title: 'Value Selling & Consultative Selling', icon: 'TrendingUp' },
  { slug: 'frameworks-methodologies', title: 'Sales Frameworks & Methodologies', icon: 'Layers' },
  { slug: 'deal-strategy', title: 'Deal Strategy & Execution', icon: 'Target' },
  { slug: 'negotiation-procurement', title: 'Negotiation, Procurement & Legal Navigation', icon: 'Handshake' },
  { slug: 'account-territory', title: 'Account Planning & Territory Strategy', icon: 'Map' },
  { slug: 'executive-selling', title: 'Executive Selling & Stakeholder Management', icon: 'Crown' },
  { slug: 'partner-ecosystem', title: 'Partner Selling & Ecosystem Management', icon: 'Users' },
  { slug: 'marketing-fundamentals', title: 'Marketing Fundamentals for Sellers', icon: 'Megaphone' },
  { slug: 'forecasting-ops', title: 'Forecasting, Pipeline Management & Sales Ops', icon: 'BarChart3' },
  { slug: 'ai-modern-selling', title: 'AI-Powered Selling & Modern GTM Tooling', icon: 'Cpu' },
  { slug: 'sector-adaptation', title: 'Sector-Specific Sales Adaptation', icon: 'Globe' },
  { slug: 'performance-coaching', title: 'Performance, Coaching & Continuous Improvement', icon: 'Award' },
  { slug: 'leadership-team', title: 'Leadership & Team Selling', icon: 'Flag' },
  { slug: 'evolution-engine', title: 'My Craft Evolution Engine', icon: 'RefreshCw' },
  { slug: 'external-resources', title: 'External Resources & Further Study', icon: 'ExternalLink' },
];

export const AVAILABLE_LEARNING_PATHS = [
  { slug: 'new-sdr-ramp', title: 'New SDR Ramp Path', icon: 'Rocket' },
  { slug: 'enterprise-ae-excellence', title: 'Enterprise AE Excellence Path', icon: 'Trophy' },
  { slug: 'executive-selling-path', title: 'Executive Selling Path', icon: 'Crown' },
  { slug: 'outbound-mastery', title: 'Outbound Mastery Path', icon: 'Zap' },
  { slug: 'value-selling-path', title: 'Value Selling Path', icon: 'TrendingUp' },
  { slug: 'deal-strategy-path', title: 'Deal Strategy Path', icon: 'Target' },
  { slug: 'vertical-selling', title: 'Vertical Selling Overlay Path', icon: 'Globe' },
  { slug: 'ai-modern-seller', title: 'AI-Enabled Modern Seller Path', icon: 'Cpu' },
  { slug: 'sales-manager-coaching', title: 'Sales Manager Coaching Path', icon: 'UserCheck' },
];

export const CRAFT_ROLES = [
  'SDR / BDR',
  'AE',
  'AM / CSM',
  'SE / Solutions Consultant',
  'Partner Manager',
  'Sales Manager',
  'GTM Leader',
] as const;

export const CRAFT_SEGMENTS = ['SMB', 'Mid-market', 'Enterprise', 'Strategic'] as const;

export const CRAFT_MOTIONS = [
  'Inbound',
  'Outbound',
  'Expansion / Upsell',
  'New Logo',
  'Partner-led',
  'Marketplace-led',
] as const;
